import React, { useEffect, useId, useState } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

export type FilterSectionProps = {
  id: string;
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
};

/** Collapsible group matching Ops / Admin sidebar section chrome. */
export const FilterSection: React.FC<FilterSectionProps> = ({
  id,
  label,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`filter-section-${id}`}
      >
        <span>{label}</span>
        <span aria-hidden className="text-gray-600">
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? (
        <div id={`filter-section-${id}`} className="mt-2 space-y-3 px-0.5">
          {children}
        </div>
      ) : null}
    </div>
  );
};

interface DirectoryBrowseLayoutProps {
  /** Sidebar heading (e.g. Filters) */
  filterTitle?: string;
  /** Count shown on the mobile Filters button badge */
  activeFilterCount?: number;
  /** Filter form body (shared desktop rail + mobile drawer) */
  sidebar: React.ReactNode;
  /** Optional sticky footer actions inside the filter panel */
  sidebarFooter?: React.ReactNode;
  /** Main column: search, chips, results */
  children: React.ReactNode;
  /** Called when the mobile drawer closes */
  onMobileClose?: () => void;
  /** Controlled open state for mobile drawer (optional) */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

/**
 * Public directory layout with an Ops-style left filter rail.
 * Desktop (lg+): persistent sidebar. Mobile: drawer + overlay.
 */
export const DirectoryBrowseLayout: React.FC<DirectoryBrowseLayoutProps> = ({
  filterTitle = 'Filters',
  activeFilterCount = 0,
  sidebar,
  sidebarFooter,
  children,
  onMobileClose,
  mobileOpen: controlledOpen,
  onMobileOpenChange,
}) => {
  const reactId = useId();
  const navId = `directory-filters-${reactId.replace(/:/g, '')}`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const mobileOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setMobileOpen = (open: boolean) => {
    if (!isControlled) setUncontrolledOpen(open);
    onMobileOpenChange?.(open);
    if (!open) onMobileClose?.();
  };

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setMobileOpen is stable enough for close-on-escape
  }, [mobileOpen]);

  const filterPanel = (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.06] shrink-0">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold tracking-tight text-white">{filterTitle}</h2>
          {activeFilterCount > 0 ? (
            <p className="text-[11px] text-gray-500 mt-0.5">
              {activeFilterCount} active
            </p>
          ) : (
            <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">Narrow the directory</p>
          )}
        </div>
        <button
          type="button"
          className="lg:hidden p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-white cursor-pointer rounded-lg"
          aria-label="Close filters"
          onClick={() => setMobileOpen(false)}
        >
          <XMarkIcon className="w-5 h-5" aria-hidden />
        </button>
      </div>

      {/*
        Scroll region must own overflow; footer is a non-sticky flex sibling so
        Reset/Apply never cover the last filter controls on desktop.
      */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 space-y-4">
        {sidebar}
      </div>

      {sidebarFooter ? (
        <div className="border-t border-white/[0.06] p-3 bg-[#0c0c0c] shrink-0">
          {sidebarFooter}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="w-full">
      <div className="lg:hidden mb-4">
        <Button
          type="button"
          variant="secondary"
          className="w-full sm:w-auto min-h-[44px]"
          onClick={() => setMobileOpen(true)}
          aria-expanded={mobileOpen}
          aria-controls={navId}
        >
          <FunnelIcon className="w-5 h-5" aria-hidden />
          Filters
          {activeFilterCount > 0 ? (
            <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-md bg-orange-500/20 text-orange-300 text-xs font-semibold tabular-nums">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
      </div>

      <div className="flex gap-0 lg:gap-8 items-start">
        <aside
          id={navId}
          className={`${
            mobileOpen ? 'flex' : 'hidden'
          } lg:flex flex-col fixed lg:sticky inset-y-0 left-0 z-50 w-[min(20rem,88vw)] lg:w-64 shrink-0 border-r border-white/[0.06] lg:border lg:border-white/[0.06] lg:rounded-xl lg:top-20 lg:h-[calc(100vh-6rem)] lg:max-h-[calc(100vh-6rem)] bg-[#0c0c0c] overflow-hidden`}
          aria-label={filterTitle}
        >
          {filterPanel}
        </aside>

        {mobileOpen ? (
          <button
            type="button"
            className="fixed inset-0 bg-black/50 z-40 lg:hidden cursor-pointer"
            aria-label="Close filters overlay"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}

        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

export default DirectoryBrowseLayout;
